import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { authActions } from "../../store/auth.slice";
import { msalApp } from "../../services/GraphService";
import { loginRequest, silentRequest } from "../../helpers/authConfig";
import { authorizationToken } from "../../helpers/authorizationToken";

const PENDING_REDIRECT_KEY = "pendingAutoLoginRedirect";

const TokenHandler = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [aadReady, setAadReady] = useState(false);
  const handledRef = useRef(false);

  // AAD redirect dönüşünü yakala (loginRedirect sonrası sayfa yeniden yüklendiğinde)
  useEffect(() => {
    msalApp
      .handleRedirectPromise()
      .then((result) => {
        if (result?.accessToken) {
          const pendingRedirect = sessionStorage.getItem(PENDING_REDIRECT_KEY);
          sessionStorage.removeItem(PENDING_REDIRECT_KEY);

          // loginWithAad fulfilled reducer window.location.href = "/" yapar.
          // pendingRedirect varsa .then() içinde override edilir — son atama kazanır
          // çünkü tarayıcı henüz navigate etmeden her iki atama da aynı microtask'ta çalışır.
          dispatch(
            authActions.loginWithAad({
              email: result.account?.username,
              token: result.accessToken,
            }),
          ).then(() => {
            if (pendingRedirect) {
              window.location.href = pendingRedirect;
            }
          });
        }
      })
      .catch((err) => {
        console.error("AAD redirect handling failed:", err);
      })
      .finally(() => {
        setAadReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // myDtasToken işleme — AAD redirect handling tamamlandıktan sonra çalışır
  useEffect(() => {
    if (!aadReady) return;
    if (handledRef.current) return;

    checkAndHandleUrlToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aadReady, location.search]);

  const checkAndHandleUrlToken = async () => {
    const urlParams = new URLSearchParams(location.search);
    const urlToken = urlParams.get("myDtasToken");
    const redirectUrl = urlParams.get("redirectUrl");

    if (!urlToken) return;

    handledRef.current = true;

    if (!isAuthenticated) {
      const res = await dispatch(
        authActions.validateTokenAndLogin({ token: urlToken }),
      );
      const token = res.payload?.data?.token;
      clearParams();
      if (!token) {
        handledRef.current = false;
        return;
      }

      // Axios default header'ını güncelle
      authorizationToken(token);

      // AAD token almayı dene
      await tryAcquireAadToken(redirectUrl, token);
    } else {
      clearParams();
      if (redirectUrl) {
        handleRedirectUrl(redirectUrl, localStorage.getItem("jwtToken"));
      }
    }
  };

  const tryAcquireAadToken = async (redirectUrl, jwtToken) => {
    const account = msalApp.getActiveAccount();

    if (account) {
      try {
        silentRequest.account = account;
        const result = await msalApp.acquireTokenSilent(silentRequest);
        if (result?.accessToken) {
          // Silent başarılı — loginWithAad ile tam login (page reload tetikler)
          dispatch(
            authActions.loginWithAad({
              email: result.account?.username,
              token: result.accessToken,
            }),
          );
          return;
        }
      } catch (err) {
        console.warn(
          "Silent token acquisition failed, falling back to redirect:",
          err,
        );
      }
    }

    // Silent başarısız veya account yok — loginRedirect kullan (popup blocker yok)
    if (redirectUrl) {
      sessionStorage.setItem(PENDING_REDIRECT_KEY, redirectUrl);
    }

    try {
      await msalApp.loginRedirect(loginRequest);
    } catch (err) {
      console.error("AAD loginRedirect failed:", err);
      // AAD redirect başarısız — mevcut JWT ile devam et
      handleRedirectUrl(redirectUrl, jwtToken);
    }
  };

  const clearParams = () => {
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete("myDtasToken");
    newParams.delete("redirectUrl");

    const newUrl =
      window.location.pathname +
      (newParams.toString() ? "?" + newParams.toString() : "");
    window.history.replaceState({}, "", newUrl);
  };

  const handleRedirectUrl = (redirectUrl, token) => {
    if (!redirectUrl) {
      window.location.href = "/";
      return;
    }

    try {
      const decodedUrl = decodeURIComponent(redirectUrl);

      // İç URL kontrolü (/ ile başlayan)
      if (decodedUrl.startsWith("/")) {
        window.location.href = decodedUrl;
        return;
      }

      // Dış URL kontrolü (http veya https ile başlayan)
      if (
        decodedUrl.startsWith("http://") ||
        decodedUrl.startsWith("https://")
      ) {
        const currentOrigin = window.location.origin;
        const redirectUrlObj = new URL(decodedUrl);
        const redirectOrigin = redirectUrlObj.origin;

        if (currentOrigin === redirectOrigin) {
          window.location.href = decodedUrl;
        } else {
          const urlObj = new URL(decodedUrl);
          urlObj.searchParams.set("token", token);
          window.location.href = urlObj.toString();
        }
        return;
      }
    } catch (error) {
      console.error("Error processing redirect URL:", error);
    }

    window.location.href = "/";
  };

  return children;
};

export default TokenHandler;
